import { useEffect, useRef, useState } from 'react';
import { Frame } from './Frame';
import { KrabenMark } from './KrabenMark';
import { MoveToBrowser } from './MoveToBrowser';
import { keepContactEmail, lastContactEmail } from '../lib/contact';
import { looksLikeEmail } from '../lib/in-app';
import { inAppBrowser, inAppName, platform } from '../lib/in-app-browser';
import { markKeepAsked } from '../lib/keep-ask';
import { KEEP, browserName } from '../data/keep-copy';
import { track } from '../analytics';

/**
 * How you get back tomorrow: asked on the reward, once, just after the first
 * print has been posted (lib/keep-ask.ts says when). The email is kept on
 * their own doc and a sign-in link is sent (lib/contact.ts).
 *
 * Inside Facebook's or Instagram's browser that link is the way out: it
 * carries a handoff code (lib/handoff.ts), so wherever Mail opens it signs in
 * as this same anonymous uid and lands on the road. MoveToBrowser offers the
 * same thing without the email: the ⋯ menu on iOS, Chrome on Android, or a
 * link to copy.
 *
 * Events: keep_ask_shown, keep_email {given}, keep_email_kept {link_sent},
 * keep_done; and the handoff's own (lib/account.ts, MoveToBrowser).
 */
/** How long Send waits for the email to be kept and the link to go, before it moves on regardless. */
const SEND_WAIT_MS = 12_000;

export function KeepAsk({ day, onDone, beforeTurn }: { day: number; /** how the email went, when the road is to say it (a real browser, before the turn) */ onDone: (how?: 'sent' | 'kept') => void; /** asked before the print turns over (lean Day 1): in a real browser Send goes straight on and the road says it */ beforeTurn?: boolean }) {
  const iab = inAppBrowser();
  const os = platform();
  const browser = browserName(os);
  const [email, setEmail] = useState(lastContactEmail);
  const [note, setNote] = useState<string | null>(null);
  const [state, setState] = useState<'ask' | 'sending' | 'sent' | 'kept'>('ask');
  const [moving, setMoving] = useState(false);
  const shown = useRef(false); // once per screen, StrictMode's second mount included
  useEffect(() => {
    if (shown.current) return;
    shown.current = true;
    markKeepAsked();
    track('keep_ask_shown', { in_app: iab ?? 'none', os, prefilled: !!email });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { document.querySelector('.phone.fit .main')?.scrollTo?.(0, 0); }, [state]);

  const give = async () => {
    const e = email.trim();
    if (!looksLikeEmail(e)) { setNote(KEEP.bad); return; }
    setState('sending');
    track('keep_email', { given: true, in_app: iab ?? 'none', moving });
    // On a dead connection Firestore waits rather than failing: the screen does not wait with it.
    const out = await Promise.race([keepContactEmail(e, 'reward'), new Promise<null>((r) => setTimeout(() => r(null), SEND_WAIT_MS))]);
    track('keep_email_kept', out ? { kept: out.kept, link_sent: out.linkSent, in_app: iab ?? 'none' } : { timed_out: true, in_app: iab ?? 'none' });
    // before the turn, a real browser goes straight on to the picture; inside Facebook's the screen after Send stays,
    // since it is where the way into Safari is (MoveToBrowser), and its button turns the print over
    if (beforeTurn && !iab) { track('keep_done', { state: out?.linkSent ? 'sent' : 'kept', in_app: 'none', before_turn: true }); onDone(out?.linkSent ? 'sent' : 'kept'); return; }
    setState(out?.linkSent ? 'sent' : 'kept');
  };
  const done = () => { track('keep_done', { state, in_app: iab ?? 'none', ...(beforeTurn ? { before_turn: true } : {}) }); onDone(); };

  if (state === 'sent' || state === 'kept') {
    const sent = state === 'sent';
    return (
      <Frame theme="fit" left={<span className="tag">Day {day}</span>}>
        <div className="row between"><span className="label">{KEEP.label}</span><KrabenMark size={56} /></div>
        <h1>{sent ? KEEP.sentTitle : KEEP.keptTitle}</h1>
        <p className="why" style={{ fontSize: 17 }}>{sent ? (iab ? KEEP.sentInApp(browser) : KEEP.sentBody) : KEEP.keptBody}</p>
        {iab && <MoveToBrowser from="reward" />}
        <div className="stack" style={{ marginTop: 'auto' }}>
          <button className="btn" onClick={done}>{beforeTurn ? KEEP.turn.go : KEEP.done}</button>
          <div className="row" style={{ justifyContent: 'center' }}>
            <button className="link" onClick={() => setState('ask')}>{sent ? KEEP.other : KEEP.again}</button>
          </div>
        </div>
      </Frame>
    );
  }

  const sending = state === 'sending';
  return (
    <Frame theme="fit" left={<span className="tag">Day {day}</span>}>
      <div className="row between"><span className="label">{beforeTurn ? KEEP.turn.label : KEEP.label}</span><KrabenMark size={56} /></div>
      <h1>{beforeTurn ? KEEP.turn.title : KEEP.title}</h1>
      <p className="why" style={{ fontSize: 17 }}>{iab ? KEEP.inApp(inAppName(iab), browser) : beforeTurn ? KEEP.turn.body : KEEP.body}</p>
      <form className="stack" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); void give(); }}>
        <input className="input big" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} enterKeyHint="send"
          placeholder={KEEP.placeholder} aria-label="Your email" value={email} onChange={(e) => { setEmail(e.target.value); setNote(null); }} />
        {note && <p className="hint" role="alert" style={{ color: 'var(--coral-dark)' }}>{note}</p>}
      </form>
      {moving && <MoveToBrowser from="reward" />}
      <div className="stack" style={{ marginTop: 'auto' }}>
        <button className="btn coral" disabled={!email.trim() || sending} onClick={() => void give()}>{sending ? KEEP.waiting : KEEP.send}</button>
        <div className="row" style={{ justifyContent: 'center', gap: 18, flexWrap: 'wrap' }}>
          {iab && !moving && <button className="link" disabled={sending} onClick={() => { track('keep_move_instead', { os }); setMoving(true); }}>{KEEP.moveInstead(browser)}</button>}
          <button className="link" disabled={sending} onClick={() => { track('keep_email', { given: false, in_app: iab ?? 'none', moving }); done(); }}>{moving ? (beforeTurn ? KEEP.turn.go : KEEP.done) : beforeTurn ? KEEP.turn.skip : KEEP.skip}</button>
        </div>
      </div>
    </Frame>
  );
}
